import { Link } from "react-router";
import { motion } from "motion/react";
import {
  ArrowRight,
  Check,
  Sparkles,
  Shield,
  Zap,
  Clock,
  DollarSign,
  Rocket,
  Building,
  Briefcase,
  Users,
} from "lucide-react";

export function Home() {
  const problems = [
    "Baukästen überfordern und sehen unprofessionell aus",
    "Agenturen sind teuer für einfache & kleine Websites",
    "IT-Wartung kostet Zeit und Nerven",
    "Die Website wird nicht aktuell gehalten",
  ];

  const solutions = [
    {
      icon: <DollarSign className="w-6 h-6" />,
      title: "Festpreis durch Automatisierung",
      description:
        "500 € Grundpreis + 99 € monatlich, jeweils zzgl. gesetzlicher MwSt. Alles inklusive. Keine versteckten Kosten.",
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: "Professionell & Individuell",
      description:
        "Von der Anfrage bis zum Launch – strukturiert, transparent, einzigartig.",
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Alles aus einer Hand",
      description:
        "Hosting, Wartung, Sicherheit, DSGVO, Barrierefreiheit – alles von uns erledigt.",
    },
  ];

  const targetGroups = [
    {
      icon: <Rocket className="w-8 h-8" />,
      title: "Gründer & Startups",
      description:
        "Professioneller Start mit geringem Budget. Keine Zeit für Technik? Wir übernehmen alles.",
    },
    {
      icon: <Building className="w-8 h-8" />,
      title: "Kleine & mittlere Unternehmen",
      description:
        "Moderne Website ohne IT-Abteilung. Wartung, DSGVO und Support inklusive.",
    },
    {
      icon: <Briefcase className="w-8 h-8" />,
      title: "B2B-Unternehmen",
      description:
        "Professionelle Präsenz für Geschäftskunden. Schnell, seriös, zum Festpreis.",
    },
  ];

  const steps = [
    {
      number: "01",
      title: "Fragebogen ausfüllen",
      description:
        "Teilen Sie uns Ihre Anforderungen mit – schnell und unkompliziert.",
    },
    {
      number: "02",
      title: "Entwurf erhalten",
      description:
        "Wir erstellen einen professionellen Website-Entwurf und senden Ihnen einen geschützten Vorschau-Link per E-Mail.",
    },
    {
      number: "03",
      title: "Entwurf prüfen & bestellen",
      description:
        "Wenn der Entwurf passt, schließen Sie die Bestellung ab und zahlen den Grundpreis von 500 € zzgl. gesetzlicher MwSt.",
    },
    {
      number: "04",
      title: "Zugang einrichten",
      description:
        "Nach erfolgreicher Bestellung vergeben Sie Ihr Passwort für den Kundenbereich und übermitteln Inhalte oder Zugangsdaten.",
    },
    {
      number: "05",
      title: "Finalisierung & Livegang",
      description:
        "Wir setzen Ihre Website final um, schalten sie live und übernehmen danach den laufenden Betrieb.",
    },
  ];

  const features = [
    "Professionelles Design",
    "Hosting inklusive",
    "Responsive für alle Geräte",
    "Regelmäßige Updates",
    "SSL-Verschlüsselung",
    "Support inklusive",
    "DSGVO-konform",
    "Barrierefreiheit",
  ];

  const logos = [
    {
      src: "/images/logo_his.webp",
      alt: "HIS – Handwerks- & Industriebedarf Schwarzheide GmbH",
      name: "HIS – Handwerks- & Industriebedarf Schwarzheide GmbH",
      className: "max-h-10",
    },
    {
      src: "/images/logo_inclusia.png",
      alt: "Inclusia Deutschland e.V. – Dresden",
      name: "Inclusia Deutschland e.V. – Dresden",
      className: "max-h-10",
    },
    {
      src: "/images/logo_nbsw.svg",
      alt: "nbsw nahverkehrsberatung PartG – Heidelberg",
      name: "nbsw nahverkehrsberatung PartG – Heidelberg",
      className: "max-h-10",
    },
    {
      src: "/images/logo_bbc.svg",
      alt: "BBC – Bar Brasserie Cassis – Dresden",
      name: "BBC – Bar Brasserie Cassis – Dresden",
      className: "max-h-8",
    },
    {
      src: "/images/logo_eloquens.png",
      alt: "Eloquens BGK Sprachschule GmbH – Köln",
      name: "Eloquens BGK Sprachschule GmbH – Köln",
      className: "max-h-10",
    },
  ];

  const portfolioItems = [
    {
      src: "/images/portfolio_1.png",
      alt: "Portfolio-Beispiel 1",
      label: "Projektbeispiel 01",
    },
    {
      src: "/images/portfolio_2.png",
      alt: "Portfolio-Beispiel 2",
      label: "Projektbeispiel 02",
    },
    {
      src: "/images/portfolio_3.png",
      alt: "Portfolio-Beispiel 3",
      label: "Projektbeispiel 03",
    },
  ];

  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 },
  };

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div {...fadeInUp}>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-black/5 rounded-full mb-6">
                <Sparkles className="w-4 h-4" />
                <span className="text-sm">Website as a Service</span>
              </div>
              <h1 className="text-5xl lg:text-6xl xl:text-7xl mb-6 leading-tight">
                Professionelle
                <br />
                Website für{" "}
                <span className="relative inline-block">
                  <span className="relative z-10">
                    500 €
                    <span className="align-super text-[0.38em] leading-none ml-1">
                      *
                    </span>
                  </span>
                  <span className="absolute bottom-2 left-0 right-0 h-3 bg-yellow-300/50 -z-0" />
                </span>
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Plus 99 €
                <span className="align-super text-[0.6em] leading-none ml-1">*</span>{" "}
                monatlich für Hosting, Wartung, Sicherheit & 2-Stunden Anpassungen. Damit Sie bei Ihrem Kerngeschäft
                bleiben können.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/fragebogen"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-black text-white hover:bg-gray-900 transition-all duration-300 hover:scale-105 group"
                >
                  Direkt beginnen
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link
                  to="/angebot"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 border-2 border-black text-black hover:bg-black hover:text-white transition-all duration-300"
                >
                  Mehr erfahren
                </Link>
              </div>
              <div className="mt-8 flex items-center gap-8">
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-green-600" />
                  <span className="text-sm text-gray-600">Festpreis garantiert</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-green-600" />
                  <span className="text-sm text-gray-600">Wartung inklusive</span>
                </div>
              </div>
              <p className="mt-3 text-sm text-gray-500">* zzgl. Mwst.</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="/images/photo-1553877522-43269d4ea984.avif"
                  alt="Modern workspace"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-white p-6 rounded-xl shadow-xl border border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <Clock className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Website in</div>
                    <div className="text-xl">7-14 Tagen</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl mb-6">
              Die klassischen Probleme
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Viele Unternehmen kämpfen mit denselben Herausforderungen bei der
              Website-Erstellung
            </p>
          </motion.div>
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {problems.map((problem, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="flex items-start gap-4 p-6 bg-gray-50 rounded-xl"
              >
                <div className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-red-600 text-sm">✕</span>
                </div>
                <p className="text-gray-700">{problem}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl mb-6">Unsere Lösung</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Ein klarer Prozess, transparente Preise und professionelle
              Betreuung
            </p>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-8">
            {solutions.map((solution, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-xl transition-shadow duration-300"
              >
                <div className="w-14 h-14 bg-black rounded-xl flex items-center justify-center text-white mb-6">
                  {solution.icon}
                </div>
                <h3 className="text-xl font-semibold mb-3">{solution.title}</h3>
                <p className="text-gray-600 leading-relaxed">
                  {solution.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Target Groups Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl mb-6">Zielgruppen</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Für alle Unternehmengrößen und Branchen
            </p>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-8">
            {targetGroups.map((group, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-xl transition-shadow duration-300"
              >
                <div className="w-14 h-14 bg-black rounded-xl flex items-center justify-center text-white mb-6">
                  {group.icon}
                </div>
                <h3 className="text-xl font-semibold mb-3">{group.title}</h3>
                <p className="text-gray-600 leading-relaxed">
                  {group.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl mb-6">
              Diese Kunden haben uns bereits vertraut
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="w-full"
          >
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {logos.map((logo) => (
                <div
                  key={logo.alt}
                  className="group flex h-24 flex-col items-center justify-center px-5"
                >
                  <img
                    src={logo.src}
                    alt={logo.alt}
                    className={`${logo.className} w-full object-contain grayscale opacity-75 transition duration-300 group-hover:grayscale-0 group-hover:opacity-100`}
                    loading="lazy"
                  />
                  <span className="mt-3 text-center text-sm text-gray-700 opacity-0 transition duration-300 group-hover:opacity-100">
                    {logo.name}
                  </span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Portfolio Preview Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl mb-4">
              Ein Eindruck unserer Arbeiten
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Reduziert, klar und auf den ersten Blick nachvollziehbar.
            </p>
          </motion.div>

          <div className="grid gap-6 md:grid-cols-3">
            {portfolioItems.map((item, index) => (
              <motion.div
                key={item.src}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.08 }}
                className="group"
              >
                <div className="overflow-hidden rounded-3xl bg-gray-100">
                  <div className="aspect-[16/10]">
                    <img
                      src={item.src}
                      alt={item.alt}
                      loading="lazy"
                      className="h-full w-full object-cover grayscale-0 transition duration-500 md:grayscale md:group-hover:scale-[1.02] md:group-hover:grayscale-0"
                    />
                  </div>
                </div>
                <div className="mt-4 flex items-center justify-between text-sm text-gray-500">
                  <span>{item.label}</span>
                  <span className="h-px w-12 bg-black/15 transition duration-300 group-hover:w-16" />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl mb-6">Unser Website-Paket</h2>
            <p className="text-xl text-gray-600">
              Professionell umgesetzt, individuell zugeschnitten, langfristig betreut
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto"
          >
            <div className="bg-gradient-to-br from-gray-900 to-black text-white rounded-3xl p-12 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
              <div className="relative z-10">
                <div className="grid md:grid-cols-2 gap-12 mb-12">
                  <div>
                    <div className="text-sm text-gray-400 mb-2">Einmalig</div>
                    <div className="text-6xl mb-4">500 €</div>
                    <p className="text-gray-300">
                      Setup, Erstellung und Launch Ihrer Website zzgl. gesetzlicher MwSt.
                    </p>
                  </div>
                  <div>
                    <div className="text-sm text-gray-400 mb-2">Monatlich</div>
                    <div className="text-6xl mb-4">99 €</div>
                    <p className="text-gray-300">
                      Hosting, Wartung, Support und Updates zzgl. gesetzlicher MwSt.
                    </p>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4 mb-8">
                  {features.map((feature, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <Check className="w-5 h-5 text-green-400" />
                      <span className="text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
                <Link
                  to="/fragebogen"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-white text-black hover:bg-gray-100 transition-all duration-300 group"
                >
                  Jetzt starten
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <p className="mt-4 text-sm text-gray-400">
                  * Alle Preise verstehen sich zzgl. gesetzlicher MwSt. Für Neukunden
                  gilt eine Mindestlaufzeit von 12 Monaten.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
	            <h2 className="text-4xl lg:text-5xl mb-6">Ablauf in 5 Schritten</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Von der Anfrage bis zum Launch – transparent und strukturiert
            </p>
          </motion.div>
	          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-8">
	            {steps.map((step, index) => (
	              <motion.div
	                key={index}
	                initial={{ opacity: 0, y: 20 }}
	                whileInView={{ opacity: 1, y: 0 }}
	                viewport={{ once: true }}
	                transition={{ duration: 0.6, delay: index * 0.1 }}
	                className="relative h-full"
	              >
	                {index < steps.length - 1 && (
	                  <div className="hidden lg:block absolute top-12 left-full w-full h-0.5 bg-gray-200 -translate-x-1/2" />
	                )}
	                <div className="relative z-10 bg-white p-6 rounded-xl h-full flex flex-col">
	                  <div className="text-5xl font-light text-gray-200 mb-4">
	                    {step.number}
	                  </div>
	                  <h3 className="text-lg font-semibold mb-2">{step.title}</h3>
	                  <p className="text-sm text-gray-600 leading-relaxed">
	                    {step.description}
	                  </p>
	                </div>
	              </motion.div>
	            ))}
	          </div>
	        </div>
	      </section>

      {/* CTA Section */}
      <section className="py-24 bg-black text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <Users className="w-16 h-16 mx-auto mb-6 opacity-80" />
            <h2 className="text-4xl lg:text-5xl mb-6">
              Bereit für Ihre professionelle Website?
            </h2>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              Starten Sie jetzt mit unserem Fragebogen und erhalten Sie in wenigen
              Tagen Ihren Website-Entwurf.
            </p>
            <Link
              to="/fragebogen"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-black hover:bg-gray-100 transition-all duration-300 group"
            >
              Website starten
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
